import React, { useEffect, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CampaignTable, CampaignDrawerDetails, PageLayout, PageHeader } from '../../components'
import { useCampaignData, useCampaignDrawerData, useDateRangeWithURL, useDrawerURL } from '../../hooks'
import { useAutoSync } from '../../hooks/useAutoSync'
import styles from './AdminCampaignList.module.scss'

const AdminCampaignListPage = () => {
  const { t } = useTranslation()

  useAutoSync()

  const { monthStart, today } = useMemo(() => {
    const start = new Date()
    start.setDate(1)
    start.setHours(12, 0, 0, 0)
    const end = new Date()
    end.setHours(12, 0, 0, 0)
    return { monthStart: start, today: end }
  }, [])

  const {
    startDate,
    endDate,
    handleStartDateChange,
    handleEndDateChange
  } = useDateRangeWithURL(monthStart, today)

  const [showHistoricalData, setShowHistoricalData] = React.useState(false)

  const [showInactiveCampaigns, setShowInactiveCampaigns] = React.useState(false)

  const isInitialMount = useRef(true)

  const {
    campaigns,
    loading,
    error,
    fiveYearsAgo,
    fetchCampaigns,
    initializeData,
    getPlanRemainingColor
  } = useCampaignData({
    showHistoricalData,
    showInactiveCampaigns,
    startDate,
    endDate
  })

  const {
    drawerOpen,
    selectedCampaign,
    campaignMetrics,
    loadingMetrics,
    handleCampaignClick,
    handleDrawerClose,
    fetchCampaignMetrics
  } = useCampaignDrawerData({
    startDate,
    endDate
  })

  const handleOpenFromURL = useCallback((campaignName) => {
    const decodedName = decodeURIComponent(campaignName)
    const campaign = campaigns.find(c => c.campaign_name === decodedName)
      || { campaign_name: decodedName }
    handleCampaignClick(campaign)
  }, [campaigns, handleCampaignClick])

  const { openDrawerWithURL, closeDrawerWithURL } = useDrawerURL('campaign', {
    drawerOpen,
    onOpenFromURL: handleOpenFromURL,
    onClose: handleDrawerClose
  })

  const handleCampaignClickWithURL = (campaign) => {
    handleCampaignClick(campaign)
    openDrawerWithURL(encodeURIComponent(campaign.campaign_name))
  }

  const handleDrawerCloseWithURL = () => {
    handleDrawerClose()
    closeDrawerWithURL()
  }

  useEffect(() => {
    initializeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    fetchCampaigns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistoricalData, showInactiveCampaigns])

  useEffect(() => {
    if (drawerOpen && selectedCampaign) {
      fetchCampaignMetrics(selectedCampaign.campaign_name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, drawerOpen, selectedCampaign])

  /* istanbul ignore next -- AriaDatePicker onChange requires native calendar interaction in jsdom */
  const handleDateRangeChange = (start, end) => {
    if (start) {
      const startEvent = {
        target: {
          value: start.toISOString().split('T')[0]
        }
      }
      handleStartDateChange(startEvent)
    }
    if (end) {
      const endEvent = {
        target: {
          value: end.toISOString().split('T')[0]
        }
      }
      handleEndDateChange(endEvent)
    }
  }

  if (error) {
    return (
      <Alert severity='error' className={styles.errorAlert}>
        Error: {error}
      </Alert>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title={t('campaigns.title')}
        subtitle={t('analytics.subtitle')}
        actions={
          <div className={styles.segmentedGroup}>
            <button
              type='button'
              className={`${styles.segmentedButton} ${!showHistoricalData ? styles.segmentedButtonActive : ''}`}
              onClick={() => {
                if (showHistoricalData) {
                  setShowHistoricalData(false)
                }
              }}
            >
              {t('analytics.dateRange.preset.thisMonth')}
            </button>
            <button
              type='button'
              className={`${styles.segmentedButton} ${showHistoricalData ? styles.segmentedButtonActive : ''}`}
              onClick={() => {
                if (!showHistoricalData) {
                  setShowHistoricalData(true)
                  setShowInactiveCampaigns(true)
                }
              }}
            >
              {t('analytics.dateRange.preset.lastYear')}
            </button>
          </div>
        }
      />

        {/* Data range info banner */}
        <div className={`${styles.infoBanner} ${showHistoricalData ? styles.historical : styles.monthToDate}`}>
          <span className={`${styles.infoIcon} ${showHistoricalData ? styles.historical : styles.monthToDate}`}>
            <FontAwesomeIcon
              icon='calendar-alt'
              className={`${styles.calendarIcon} ${showHistoricalData ? styles.historical : styles.monthToDate}`}
            />
          </span>
          <Typography variant='body2' className={styles.infoText}>
            {showHistoricalData
              ? (
                <>
                  {t('analytics.viewingCampaign', {
                    campaign: t('analytics.dateRange.preset.lastYear')
                  })}{' '}
                  <strong>{fiveYearsAgo.toLocaleDateString()}</strong> {t('common.to')}{' '}
                  <strong>{endDate.toLocaleDateString()}</strong>
                </>
                )
              : (
                <>
                  {t('analytics.viewingCampaign', {
                    campaign: t('analytics.dateRange.preset.thisMonth')
                  })}{' '}
                  <strong>{startDate.toLocaleDateString()}</strong> {t('common.to')}{' '}
                  <strong>{endDate.toLocaleDateString()}</strong>
                </>
                )}
          </Typography>

          {!loading && campaigns.length > 0 && (
            <span className={styles.campaignCount}>
              <strong>{campaigns.length}</strong> campaigns
              {!showHistoricalData && !showInactiveCampaigns && (
                <span className={styles.filterBadge}>Active only</span>
              )}
            </span>
          )}

          {/* Show inactive campaigns toggle - only visible in month-to-date view */}
          {!showHistoricalData && (
            <button
              type='button'
              className={`${styles.toggleChip} ${showInactiveCampaigns ? styles.toggleChipActive : ''}`}
              onClick={() => setShowInactiveCampaigns(!showInactiveCampaigns)}
              aria-pressed={showInactiveCampaigns}
            >
              <FontAwesomeIcon
                icon={showInactiveCampaigns ? 'eye' : 'eye-slash'}
                className={styles.toggleChipIcon}
              />
              {t('campaigns.inactive')}
            </button>
          )}
        </div>

      {/* Campaign table - using our extracted component */}
      <CampaignTable
        campaigns={campaigns}
        loading={loading}
        showHistoricalData={showHistoricalData}
        showInactiveCampaigns={showInactiveCampaigns}
        startDate={startDate}
        endDate={endDate}
        getPlanRemainingColor={getPlanRemainingColor}
        onCampaignDetailsClick={handleCampaignClickWithURL}
      />

      {/* Campaign metrics drawer - using our extracted component */}
      <CampaignDrawerDetails
        drawerOpen={drawerOpen}
        onClose={handleDrawerCloseWithURL}
        selectedCampaign={selectedCampaign}
        campaignMetrics={campaignMetrics}
        loadingMetrics={loadingMetrics}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
      />
    </PageLayout>
  )
}

export default AdminCampaignListPage
